import { ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { useDeleteChangeEventMutation } from "src/routes/projects/change-events/ChangeEventPage.generated";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import {
  WorkflowActionArgs,
  WorkflowActions,
  WorkflowPageActionArgs,
} from "src/routes/projects/change-events/guided/workflows/types";
import { useGuidedChangeEventActions } from "../sharedSteps/useGuidedChangeEventActions";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";

/**
 * Saves the apply configuration change event.
 *
 * A config change's `target` can't be updated on BE (its composed `result` would describe the old config), so
 * picking a different configuration on an already-saved draft deletes that draft and creates a new one.
 */
export function useSaveApplyConfigurationChangeEvent(args: WorkflowActionArgs<ApplyConfigurationFormInput>) {
  const { form } = args;
  const [deleteChangeEvent] = useDeleteChangeEventMutation();
  const { saveChangeEvent: saveGuidedChangeEvent } = useGuidedChangeEventActions(args, (form) => ({
    configChange: {
      id: form.configChangeId.changedValue ?? undefined,
      targetId: form.targetConfigId.value,
      ...(form.workflow.value === ChangeEventWorkflow.ApplyInitialConfiguration
        ? {}
        : { optionIds: form.optionIds.value }),
    },
  }));

  async function saveChangeEvent(opts?: { andExit?: boolean; dryRun?: boolean }): Promise<void> {
    // Delete first, then create
    if (form.id.value && form.targetConfigId.dirty && form.canSave()) {
      await deleteChangeEvent({ variables: { input: { id: form.id.value } } });
      // `targetConfigId` stays dirty, so the save below still goes through, as a create
      form.set({ id: undefined, configChangeId: undefined });
    }
    return saveGuidedChangeEvent(opts);
  }

  return { saveChangeEvent };
}

/** The actions the initial and partial apply-configuration workflows share. */
export function useApplyConfigurationActions(
  args: WorkflowPageActionArgs<ApplyConfigurationFormInput>,
): WorkflowActions {
  const { form, project } = args;
  const { saveChangeEvent } = useSaveApplyConfigurationChangeEvent(args);
  return {
    // TODO: submit the change event for the *partial* apply-configuration flow. The initial workflow overrides
    // this with its own signed-contract branch.
    onSubmit: async () => {},
    onContinue: {
      [GuidedChangeEventStep.SelectConfiguration]: async () => {
        // With no signed contract the configuration is applied directly later, so there is no draft to create;
        // the save is only a dry run, so the Review Impact / Review steps preview the real derivation from the cache.
        await saveChangeEvent({ dryRun: !project.latestActiveStage?.hasSignedContract });
      },
    },
    onSaveAndExit: form.value.id ? () => saveChangeEvent({ andExit: true }) : undefined,
  };
}
